
import React, { useState } from 'react'
import { Plus, Eye, Edit, Trash2, FileText, Download, Search, Filter, Calendar, DollarSign, Target, TrendingUp, Settings, History, FileDown, X } from 'lucide-react'
import { useContratos } from '../hooks/useContratos'
import MetricsCard from '../components/MetricsCard'
import ContratoForm from '../components/ContratoForm'
import TemplateContratoForm from '../components/TemplateContratoForm'
import HistoricoContrato from '../components/HistoricoContrato'
import NotificationBellContratos from '../components/NotificationBellContratos'
import { exportContrateToPDF, exportContratoToWord } from '../utils/contratoExportUtils'

const Contratos: React.FC = () => {
  const {
    contratos,
    templates,
    loading,
    metrics,
    createContrato,
    updateContrato,
    deleteContrato,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getOrcamentosAprovados
  } = useContratos()

  const [showContratoForm, setShowContratoForm] = useState(false)
  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [editingContrato, setEditingContrato] = useState<any>(null)
  const [editingTemplate, setEditingTemplate] = useState<any>(null)
  const [viewingHistory, setViewingHistory] = useState<{ id: string; numero: string } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'contratos' | 'templates'>('contratos')
  const [orcamentosAprovados, setOrcamentosAprovados] = useState<any[]>([])
  const [showOrcamentoModal, setShowOrcamentoModal] = useState(false)
  const [selectedOrcamento, setSelectedOrcamento] = useState<any>(null)

  // Load orçamentos aprovados
  React.useEffect(() => {
    const loadOrcamentosAprovados = async () => {
      const orcamentos = await getOrcamentosAprovados()
      setOrcamentosAprovados(orcamentos)
    }
    loadOrcamentosAprovados()
  }, [getOrcamentosAprovados])

  // Filter contratos
  const filteredContratos = contratos.filter(contrato => {
    const matchesSearch = contrato.nomeCliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contrato.nomeProjeto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contrato.numero.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || contrato.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Calculate percentage change
  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'em_desenvolvimento': return 'bg-yellow-100 text-yellow-800'
      case 'enviado': return 'bg-blue-100 text-blue-800'
      case 'aprovado': return 'bg-green-100 text-green-800'
      case 'perdido': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'em_desenvolvimento': return 'Em Desenvolvimento'
      case 'enviado': return 'Enviado'
      case 'aprovado': return 'Aprovado'
      case 'perdido': return 'Perdido'
      default: return status
    }
  }

  // Handle delete
  const handleDelete = async (id: string, type: 'contrato' | 'template') => {
    if (confirm(`Tem certeza que deseja excluir este ${type}?`)) {
      if (type === 'contrato') {
        await deleteContrato(id)
      } else {
        await deleteTemplate(id)
      }
    }
  }

  // Handle view history
  const handleViewHistory = (contrato: any) => {
    setViewingHistory({
      id: contrato._id,
      numero: contrato.numero
    })
  }

  // Handle create from orçamento
  const handleCreateFromOrcamento = (orcamento: any) => {
    setSelectedOrcamento(orcamento)
    setShowContratoForm(true)
    setShowOrcamentoModal(false)
  }

  // Handle open orçamento modal
  const handleOpenOrcamentoModal = () => {
    setShowOrcamentoModal(true)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando contratos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Contratos</h1>
              <p className="text-gray-600 mt-1">Gestão completa de contratos e documentos</p>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBellContratos />
              <div className="flex gap-2">
                <button
                  onClick={handleOpenOrcamentoModal}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
                >
                  <Plus size={16} />
                  Gerar de Orçamento
                </button>
                <button
                  onClick={() => setShowContratoForm(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
                >
                  <Plus size={20} />
                  Novo Contrato
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Total de Contratos"
            value={metrics.totalContratos}
            icon={FileText}
            color="#3B82F6"
            description={`${calculatePercentageChange(metrics.totalContratos, metrics.totalContratosAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.totalContratos, metrics.totalContratosAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Valor Total"
            value={formatCurrency(metrics.valorTotal)}
            icon={DollarSign}
            color="#10B981"
            description={`${calculatePercentageChange(metrics.valorTotal, metrics.valorTotalAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.valorTotal, metrics.valorTotalAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Aprovados"
            value={metrics.quantidadeAprovada}
            icon={Target}
            color="#059669"
            description={`${calculatePercentageChange(metrics.quantidadeAprovada, metrics.quantidadeAprovadaAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.quantidadeAprovada, metrics.quantidadeAprovadaAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Fechados"
            value={metrics.quantidadeFechada}
            icon={TrendingUp}
            color="#F59E0B"
            description={`${calculatePercentageChange(metrics.quantidadeFechada, metrics.quantidadeFechadaAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.quantidadeFechada, metrics.quantidadeFechadaAnterior).toFixed(1)}% vs mês anterior`}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('contratos')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'contratos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Contratos ({contratos.length})
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'templates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Templates ({templates.filter(t => t.ativo).length})
              </button>
            </nav>
          </div>

          {/* Contratos Tab */}
          {activeTab === 'contratos' && (
            <>
              {/* Filtros e Busca */}
              <div className="p-6 border-b">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Buscar por cliente, projeto ou número..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Todos os Status</option>
                      <option value="em_desenvolvimento">Em Desenvolvimento</option>
                      <option value="enviado">Enviado</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="perdido">Perdido</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Lista de Contratos */}
              <div className="overflow-x-auto">
                {filteredContratos.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum contrato encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Tente ajustar os filtros de busca'
                        : 'Comece criando seu primeiro contrato'
                      }
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Número
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Projeto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data Emissão
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Validade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredContratos.map((contrato) => (
                        <tr key={contrato._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{contrato.numero}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{contrato.nomeCliente}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{contrato.nomeProjeto}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(contrato.valorTotal)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contrato.status)}`}>
                              {getStatusLabel(contrato.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(contrato.dataEmissao)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(contrato.dataValidade)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewHistory(contrato)}
                                className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                                title="Ver Histórico"
                              >
                                <History size={16} />
                              </button>
                              <button
                                onClick={() => setEditingContrato(contrato)}
                                className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => exportContrateToPDF(contrato)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="Exportar PDF"
                              >
                                <Download size={16} />
                              </button>
                              <button
                                onClick={() => exportContratoToWord(contrato)}
                                className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                title="Exportar Word"
                              >
                                <FileDown size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(contrato._id, 'contrato')}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <>
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Templates de Contrato</h3>
                  <button
                    onClick={() => setShowTemplateForm(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Novo Template
                  </button>
                </div>
              </div>

              <div className="p-6">
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <Settings className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum template criado</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Crie templates para agilizar a criação de contratos
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map(template => (
                      <div key={template._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{template.nome}</h4>
                            <p className="text-sm text-gray-600">{template.categoria}</p>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            template.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {template.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-4">{template.descricao}</p>
                        
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-sm text-gray-500">Validade: {template.prazoValidadePadrao} dias</span>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingTemplate(template)}
                            className="flex-1 bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700 text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(template._id, 'template')}
                            className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Seleção de Orçamentos Aprovados */}
      {showOrcamentoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Selecionar Orçamento Aprovado</h2>
                <button
                  onClick={() => setShowOrcamentoModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {orcamentosAprovados.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum orçamento aprovado encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Primeiro aprove alguns orçamentos para gerar contratos
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {orcamentosAprovados.map(orcamento => (
                    <div
                      key={orcamento._id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleCreateFromOrcamento(orcamento)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900">{orcamento.numero}</h4>
                          <p className="text-gray-600">{orcamento.nomeCliente}</p>
                          <p className="text-sm text-gray-500">{orcamento.nomeProjeto}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">
                            {formatCurrency(orcamento.valorTotal)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDate(orcamento.dataEmissao)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ContratoForm
        isOpen={showContratoForm}
        onClose={() => {
          setShowContratoForm(false)
          setSelectedOrcamento(null)
        }}
        onSubmit={createContrato}
        title="Criar Novo Contrato"
        orcamentoData={selectedOrcamento}
      />

      <ContratoForm
        isOpen={!!editingContrato}
        onClose={() => setEditingContrato(null)}
        onSubmit={(data) => updateContrato(editingContrato._id, data)}
        initialData={editingContrato}
        title="Editar Contrato"
      />

      <TemplateContratoForm
        isOpen={showTemplateForm}
        onClose={() => setShowTemplateForm(false)}
        onSubmit={createTemplate}
        title="Criar Novo Template"
      />

      <TemplateContratoForm
        isOpen={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
        onSubmit={(data) => updateTemplate(editingTemplate._id, data)}
        initialData={editingTemplate}
        title="Editar Template"
      />

      <HistoricoContrato
        isOpen={!!viewingHistory}
        onClose={() => setViewingHistory(null)}
        contratoId={viewingHistory?.id || ''}
        contratoNumero={viewingHistory?.numero || ''}
      />
    </div>
  )
}

export default Contratos
